import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'

function timeAgo(isoStr) {
  if (!isoStr) return 'Never'
  const diff = Date.now() - new Date(isoStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

// Ordered highest-to-lowest to match display order in the card.
// Keys match the `tier` values stored in the memberships table.
const MEMBERSHIP_LEVELS = [
  { key: 'wealth_builder',  label: 'Wealth Builder', emoji: '💎', color: '#c9a84c' },
  { key: 'game_changer',    label: 'Game Changer',   emoji: '🎯', color: '#b89340' },
  { key: 'achievers_club',  label: "Achiever's",     emoji: '🌟', color: '#5f9a52' },
  { key: 'shining_star',    label: 'Shining Star',   emoji: '⭐', color: '#6688aa' },
  { key: 'debt_crusher',    label: 'Debt Crusher',   emoji: '💪', color: '#d4622e' },
  { key: 'budding_star',    label: 'Budding Star',   emoji: '🌱', color: '#a07840' },
  { key: 'dreamers',        label: 'Dreamers',       emoji: '🎭', color: '#8a8278' },
]

function fmt(n) {
  n = Math.round(n)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

export default function AdminReports({ session }) {
  const [stats, setStats] = useState({
    totalUsers: 0, totalLoans: 0, totalDebt: 0,
    totalEmiPaid: 0, activeLoans: 0, paidThisMonth: 0,
    premiumUsers: 0, giftedUsers: 0,
  })
  const [loading, setLoading] = useState(true)
  const [recentUsers, setRecentUsers] = useState([])
  const [activityUsers, setActivityUsers] = useState([])
  const [activeWindows, setActiveWindows] = useState({ h12: 0, h24: 0, d7: 0, d30: 0 })

  const fetchStats = useCallback(async () => {
    const now = new Date()
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

    const { count: userCount } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })

    const { data: loans } = await supabase
      .from('loans')
      .select('balance, emi_amount')

    const { count: paidCount } = await supabase
      .from('emi_payments')
      .select('*', { count: 'exact', head: true })
      .eq('payment_month', currentMonth)
      .eq('status', 'paid')

    const { data: allPayments } = await supabase
      .from('emi_payments')
      .select('amount_paid')
      .eq('status', 'paid')

    const { data: recent } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10)

    const { data: activity } = await supabase
      .from('profiles')
      .select('id, full_name, email, last_login_at, last_seen_at')
      .order('last_seen_at', { ascending: false, nullsFirst: false })

    const totalDebt = loans?.reduce((s, l) => s + parseFloat(l.balance || 0), 0) || 0
    const totalEmiPaid = allPayments?.reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0) || 0

    const [premiumCount, giftedCount] = await Promise.all([
      supabase.from('memberships').select('*', { count: 'exact', head: true }).eq('is_paid', true),
      supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'gifted'),
    ])

    const nowMs = Date.now()
    const activeW = { h12: 0, h24: 0, d7: 0, d30: 0 }
    ;(activity || []).forEach(u => {
      if (!u.last_seen_at) return
      const t = new Date(u.last_seen_at).getTime()
      if (t >= nowMs - 12 * 60 * 60 * 1000) activeW.h12++
      if (t >= nowMs - 24 * 60 * 60 * 1000) activeW.h24++
      if (t >= nowMs - 7 * 24 * 60 * 60 * 1000) activeW.d7++
      if (t >= nowMs - 30 * 24 * 60 * 60 * 1000) activeW.d30++
    })

    setStats({
      totalUsers: userCount || 0,
      totalLoans: loans?.length || 0,
      totalDebt,
      totalEmiPaid,
      activeLoans: loans?.length || 0,
      paidThisMonth: paidCount || 0,
      premiumUsers: premiumCount.count || 0,
      giftedUsers: giftedCount.count || 0,
    })
    setRecentUsers(recent || [])
    setActivityUsers(activity || [])
    setActiveWindows(activeW)
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const cards = [
    { label: 'Total Users',           value: stats.totalUsers,    icon: '👥', color: '#3d4a5c' },
    { label: 'Total Loans',           value: stats.totalLoans,    icon: '📋', color: '#b8441a' },
    { label: 'Total Debt (All Users)',value: fmt(stats.totalDebt),icon: '💰', color: '#c0392b' },
    { label: 'Total EMI Paid (Ever)', value: fmt(stats.totalEmiPaid), icon: '✅', color: '#4a6741' },
    { label: 'EMIs Paid This Month',  value: stats.paidThisMonth, icon: '📅', color: '#c9a84c' },
    { label: 'Active Loans',          value: stats.activeLoans,   icon: '⚡', color: '#7a5d1e' },
    { label: '✦ Premium Members',     value: stats.premiumUsers,  icon: '💎', color: '#c9a84c' },
    { label: '🎁 Gifted Premium',     value: stats.giftedUsers,   icon: '🎁', color: '#4a6741' },
  ]

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading reports...</div>
  )

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0, color: 'var(--text)' }}>Dashboard Reports</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Overview of all users and loans on DebtCrush.</p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {cards.map((card, i) => (
          <div key={i} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: '8px', padding: '1.3rem 1.5rem',
            position: 'relative', overflow: 'hidden',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)' }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
          >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: card.color }} />
            <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{card.icon}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
              {card.label}
            </div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.8rem', fontWeight: '700', color: 'var(--text)' }}>
              {card.value}
            </div>
          </div>
        ))}

        {/* Membership Tier Breakdown - removed, now shown as cards above */}

        {/* Active Users */}
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: '8px', padding: '1.3rem 1.5rem',
          position: 'relative', overflow: 'hidden',
          transition: 'transform 0.2s, box-shadow 0.2s',
          gridColumn: 'span 4'
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#6688aa' }} />
          <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>🕐</div>
          <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            Active Users
          </div>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Last 12 hours', count: activeWindows.h12 },
              { label: 'Last 24 hours', count: activeWindows.h24 },
              { label: 'Last 7 days',   count: activeWindows.d7 },
              { label: 'Last 30 days',  count: activeWindows.d30 },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{row.label}</span>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.6rem', fontWeight: '700', color: '#6688aa' }}>{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* New Users */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>New Users</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Last 10 signups</span>
        </div>
        {recentUsers.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No users yet.</div>
        ) : (
          recentUsers.map((user, i) => (
            <div key={user.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.85rem 1.4rem',
              borderBottom: i < recentUsers.length - 1 ? '1px solid var(--border)' : 'none',
              transition: 'background 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: '#0d0d0d', color: '#f5f0e8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.85rem', fontWeight: '700', flexShrink: 0
                }}>
                  {(user.full_name || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: '500', fontSize: '0.85rem', color: 'var(--text)' }}>{user.full_name || 'Unknown User'}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                    {user.phone || 'No phone'} &nbsp;·&nbsp; Joined {new Date(user.created_at).toLocaleDateString('en-IN')}
                  </div>
                </div>
              </div>
              <span style={{
                fontFamily: 'monospace', fontSize: '0.62rem',
                padding: '0.15rem 0.45rem', borderRadius: '3px',
                background: user.role === 'superadmin' ? 'rgba(201,168,76,0.18)' : user.role === 'admin' ? 'rgba(100,140,180,0.15)' : 'rgba(100,160,90,0.15)',
                color: user.role === 'superadmin' ? '#c9a84c' : user.role === 'admin' ? '#6688aa' : '#5f9a52',
                fontWeight: '500', textTransform: 'uppercase'
              }}>
                {user.role || 'user'}
              </span>
            </div>
          ))
        )}
      </div>

      {/* User Activity */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>User Activity</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Most recently active first</span>
        </div>

        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', padding: '0.5rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)', gap: '0.5rem' }}>
          {['Name', 'Email', 'Last Login', 'Last Seen'].map(h => (
            <div key={h} style={{ fontFamily: 'monospace', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{h}</div>
          ))}
        </div>

        {activityUsers.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No users yet.</div>
        ) : (
          activityUsers.map((user, i) => (
            <div key={user.id} style={{
              display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr',
              padding: '0.75rem 1.4rem', gap: '0.5rem', alignItems: 'center',
              borderBottom: i < activityUsers.length - 1 ? '1px solid var(--border)' : 'none',
              transition: 'background 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontWeight: '500', fontSize: '0.82rem', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.full_name || 'Unknown User'}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email || '—'}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: user.last_login_at ? 'var(--text)' : 'var(--text-muted)' }}>
                {timeAgo(user.last_login_at)}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: user.last_seen_at ? 'var(--text)' : 'var(--text-muted)' }}>
                {timeAgo(user.last_seen_at)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}